// Keyboard control surface for STARCODE. Kept close to the NEU.pde vocabulary where it makes
// sense (G = gravity, N = sun/fireball force field, K = audio ring, 8 = ripple, E = size cycle)
// plus the headline feature requested for this port: Cmd/Ctrl+Shift+M morphs the whole swarm
// into the word "STARCODE" and back, plus a broad extended effect set (Shift+letter mirrors
// NEU.pde's shiftHeldForNewEffects convention for its "new" 2nd-generation effects).

import { state, toggleEffect, setPermanentColor } from "./state.js";

export function installKeybinds({ particleSystem, onMorphToggle, onResetAll }) {
  window.addEventListener("keydown", (e) => {
    const k = e.key.toLowerCase();

    // Cmd (macOS) / Ctrl (Win/Linux) + Shift + key -> morph toggle / permanent color overrides.
    if (e.shiftKey && (e.metaKey || e.ctrlKey)) {
      if (k === "m") {
        e.preventDefault();
        toggleEffect("morph");
        onMorphToggle?.(state.morphOn);
      } else if (k === "d") {
        e.preventDefault();
        setPermanentColor(1);
      } else if (k === "r") {
        e.preventDefault();
        setPermanentColor(2);
      }
      return;
    }

    // Ignore further shortcuts while Cmd/Ctrl/Alt is held (but NOT Shift alone - that's used
    // below for the extended effect set), so plain letter keys don't fire during browser/OS
    // shortcuts.
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    // X / C (held, no Shift) - negative/positive attractor, exactly like NEU.pde's A/S: a
    // continuous push-away / pull-toward-centre force applied only while the key is physically
    // down. Deliberately moved off A/S onto two FREE, directly ADJACENT keys so they can be
    // played with one hand while the other stays on the mouse, and so they no longer sit on the
    // same letters as the Shift+A / Shift+S extended effects.
    if (!e.shiftKey) {
      if (k === "x") state.negAttractorHeld = true;
      if (k === "c") state.posAttractorHeld = true;
    }

    if (e.shiftKey) {
      switch (k) {
        case "s":
          toggleEffect("wellsOn");
          break;
        case "f":
          toggleEffect("galaxyDiscOn");
          break;
        case "g":
          toggleEffect("swarmOn");
          break;
        case "h":
          toggleEffect("vortexOn");
          break;
        case "j":
          toggleEffect("liquidRippleOn");
          break;
        case "k":
          toggleEffect("inkBleedOn");
          break;
        case "l":
          toggleEffect("portalOn");
          break;
        case "ö":
          toggleEffect("causticsOn");
          break;
        case "ä":
          toggleEffect("chaosBoostOn");
          break;
        default:
          break;
      }
      return;
    }

    switch (k) {
      case "g":
        toggleEffect("gravity");
        break;
      case "n":
        toggleEffect("fireball");
        break;
      case "k":
        toggleEffect("ring");
        break;
      case "8":
        toggleEffect("ripple");
        break;
      case "v":
        toggleEffect("galaxySpiralOn");
        break;
      case "l":
        toggleEffect("distortionOn");
        break;
      case "p":
        toggleEffect("fractalOn");
        break;
      case "9":
        toggleEffect("patternOn");
        break;
      case "0":
        toggleEffect("shapeChangeOn");
        break;
      case "m":
        toggleEffect("redCircleOn");
        break;
      case "ö":
        particleSystem.triggerRbBurst(90);
        break;
      case "ü":
        toggleEffect("streamsOn");
        break;
      case "b":
        toggleEffect("starSkyOn");
        break;
      case "6":
        toggleEffect("colorInvertOn");
        break;
      case "3":
        toggleEffect("spin360On");
        break;
      case "2":
        toggleEffect("scalePulseOn");
        break;
      // "1" is now a pure hold-modifier for camera pan-drag (see main.js) - no tap-toggle of its
      // own. Space has no function. Grid-Puls is toggled only via its control-bar button.
      case "4":
        toggleEffect("spiralHighOn");
        break;
      case "5":
        toggleEffect("bandExplodeOn");
        break;
      case "7":
        toggleEffect("shimmerOn");
        break;
      case "ß":
        toggleEffect("complexOn");
        break;
      case "i":
        // Quick one-shot outward shockwave burst - analogous to the Cmd+N sun impulse in NEU.pde.
        particleSystem.triggerExplosion(90);
        break;
      case "r":
        onResetAll?.();
        break;

      // --- New audio-reactive effects (free keys, grouped for one-hand playability) ---
      case "q":
        toggleEffect("bioluminescenceOn");
        break;
      case "w":
        toggleEffect("spectrumColorOn");
        break;
      case "t":
        toggleEffect("chromaWaveOn");
        break;
      case "z":
      case "y": // QWERTZ/QWERTY both, so the same physical key works on either layout
        toggleEffect("beatFlashOn");
        break;
      case "u":
        toggleEffect("audioSizeOn");
        break;
      case "o":
        toggleEffect("beatPulseOn");
        break;
      case "h":
        toggleEffect("breathOn");
        break;
      case "j":
        toggleEffect("tunnelOn");
        break;
      case "d":
        toggleEffect("helixOn");
        break;
      case "f":
        toggleEffect("strobeFieldOn");
        break;
      case "ä":
        // Toroidal wrap: particles leaving the world box re-enter on the opposite side,
        // matching NEU.pde's FireflyParticle.update() edge handling.
        toggleEffect("wrapModeOn");
        break;
      case "a":
        toggleEffect("autoMixOn");
        break;
      default:
        break;
    }
  });

  window.addEventListener("keyup", (e) => {
    const k = e.key.toLowerCase();
    if (k === "x") state.negAttractorHeld = false;
    if (k === "c") state.posAttractorHeld = false;
  });
}
